import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, validate } from "../../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import { ZoneMetadataFieldsWithDefaults, ZoneMetadataSchemaTypes } from "../../types/zoneMetadata.d.js";
import { DtoValidatorTypes } from "../../validatableClass.js";

const SCHEMA_KEY = "ZoneMetadata" as const;

@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<ZoneMetadata>>(...addSchemaKeyHook(SCHEMA_KEY))
export class ZoneMetadata extends SchemaBase<ZoneMetadata, ZoneMetadataFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public varietal?: string;

    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: false })
    public datePlanted?: Date;

    /**
     * in meters
     * @type {number}
     */
    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public emitterSpacing?: number;

    /**
     * in gallons per hour
     * @type {number}
     */
    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public emitterFlowRate?: number;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public rootClone?: string;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public vineClone?: string;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public notes?: string;

    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public zonePressureHead?: number;

    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public totalZoneFlowRate?: number;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public connectedPump?: string;

    static dtoValidators: DtoValidatorTypes<ZoneMetadataSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<ZoneMetadata, ZoneMetadataFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

ZoneMetadata.dtoValidators = getZodSchemaFromClass(
    ZoneMetadata,
) as unknown as DtoValidatorTypes<ZoneMetadataSchemaTypes>;
